/**
 * Shared value coercion + formatting helpers for list pages.
 *
 * Many list pages used to redefine these locally. Import from here instead.
 */

/**
 * Talep 768.1-3 / 771.3 — Yalnız RAKAM kabul eden, hane sınırlı alanlar için `FieldText`
 * `format` üreteci (Posta Kodu 5, TC Kimlik No 11, Vergi No 10).
 * Harf/işaret girişini yazarken eler; yapıştırmada da fazlasını keser.
 */
export function digitsOnly(maxLength) {
  return (value) => String(value ?? '').replace(/\D/g, '').slice(0, maxLength);
}

/**
 * Rakam-sınırlı alanın "doluysa tam N hane" kuralını doğrular. Boş = geçerli
 * (bu alanlar zorunlu değildir; zorunluluk ayrıca `req` ile yönetilir).
 */
export function isExactDigits(value, length) {
  const d = String(value ?? '').replace(/\D/g, '');
  if (!d) return true;
  return d.length === length;
}

/**
 * Talep 772 — SAYI alanları için giriş filtresi (ondalık + negatif serbest).
 *
 * Native `<input type="number">` KULLANILMAZ: tarayıcılar arasında tutarsızdır — Chrome
 * `e/E/+/-` yazılmasına izin verir, Firefox harf yazdırıp değeri sessizce boş sayar; bazı
 * yerel ayarlarda (tr-TR ondalık ayracı) elle yazım engellenip yalnız artır/azalt okları
 * çalışır. Proje bu kararı `FieldText` (DetailShell) içinde zaten vermişti; bu yardımcı
 * aynı davranışı özelleştirilmiş alanlara da taşır. Virgül noktaya çevrilir.
 */
export function sanitizeNumericInput(value) {
  return String(value ?? '').replace(',', '.').replace(/[^0-9.-]/g, '');
}

export function unwrapLookupItems(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.Data)) return payload.Data;
  if (Array.isArray(payload?.value)) return payload.value;
  return [];
}

export function toInt(value) {
  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) ? 0 : parsed;
}

export function toNullableInt(value) {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) ? null : parsed;
}

export function toRequiredInt(value) {
  const parsed = Number.parseInt(value, 10);
  if (Number.isNaN(parsed)) throw new Error('Invalid integer');
  return parsed;
}

export function toNullableShort(value) {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) ? null : parsed;
}

export function toDecimal(value) {
  if (value === null || value === undefined || value === '') return 0;
  const parsed = Number.parseFloat(String(value).replace(',', '.'));
  return Number.isNaN(parsed) ? 0 : parsed;
}

export function toRequiredDecimal(value) {
  const parsed = Number.parseFloat(String(value).replace(',', '.'));
  if (Number.isNaN(parsed)) throw new Error('Invalid decimal');
  return parsed;
}

export function toNullableDate(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export function toDateTimeOffset(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export function dateInputValue(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export function compareText(a, b, direction = 'asc') {
  const av = (a ?? '').toString().toLocaleLowerCase('tr-TR');
  const bv = (b ?? '').toString().toLocaleLowerCase('tr-TR');
  const cmp = av.localeCompare(bv, 'tr-TR');
  return direction === 'desc' ? -cmp : cmp;
}

export function formatMoney(value) {
  if (value === null || value === undefined) return '-';
  return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 2 }).format(Number(value) || 0);
}

export function formatNumber(value) {
  if (value === null || value === undefined) return '-';
  return new Intl.NumberFormat('tr-TR').format(Number(value) || 0);
}

export function displayDate(value) {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('tr-TR');
}

export function displayDateTime(value) {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('tr-TR');
}
